import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { TiPin } from "react-icons/ti";
import { createPortal } from 'react-dom';
import '../../styles/components/ui/UserMenu.css';

/**
 * Menú d'usuari a la capçalera (avatar).
 * Desplega les opcions de configuració de perfil, canvis de seguretat (correu, contrasenya, 2FA),
 * tutorial i tancar sessió. Gestiona rutes protegides i pre-checks per 2FA.
 * 
 * @param {Object} props - Propietats.
 * @param {string} props.username - Nom de l'usuari actual.
 * @param {string} props.role - Rol (ex: 'admin') per mostrar opcions extres.
 * @param {string} props.pfpUrl - URL de la foto de perfil.
 * @param {Function} props.onLogout - Callback per forçar el tancament de sessió.
 * @param {Function} props.onStartTutorial - Callback per iniciar el tutorial d'usuari.
 */
export default function UserMenu({ username, role, pfpUrl, onLogout, onStartTutorial, onOpenPinned }) {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [logoutClosing, setLogoutClosing] = useState(false);
  const [tutorialModalVisible, setTutorialModalVisible] = useState(false);
  const redirectTo = location.state?.redirectTo || "/new";
  const { theme, toggleTheme } = useTheme();

  const handleTutorialClick = () => {
    setOpen(false);
    const path = location.pathname;
    const isOnChatPage = path === '/new' || path.startsWith('/chat/');
    if (isOnChatPage) {
      onStartTutorial?.();
    } else {
      setTutorialModalVisible(true);
    }
  };

  const confirmTutorial = () => {
    setTutorialModalVisible(false);
    navigate('/new');
    // Small delay so /new renders before the tutorial overlay mounts
    setTimeout(() => onStartTutorial?.(), 100);
  };


  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  const closeAndNavigate = (path, state) => {
    setOpen(false);
    navigate(path, { state });
  };

  const openLogoutModal = () => {
    setOpen(false);
    setLogoutClosing(false);
    setLogoutModalVisible(true);
  };

  const closeLogoutModal = () => {
    setLogoutClosing(true);
    setTimeout(() => {
      setLogoutModalVisible(false);
      setLogoutClosing(false);
    }, 220);
  };

  const confirmLogout = () => {
    setLogoutClosing(true);
    setTimeout(() => {
      setLogoutModalVisible(false);
      setLogoutClosing(false);
      onLogout?.();
    }, 220);
  };

  return (
    <div id="onb-user-menu" className="header-user-menu" ref={menuRef}>
      <button
        type="button"
        className={`header-user-menu-btn${open ? ' open' : ''}`}
        onClick={() => setOpen(value => !value)}
        aria-label="Obrir menú d'usuari"
        aria-expanded={open}
        style={{
          backgroundImage: pfpUrl ? `url(${pfpUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!pfpUrl && (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="var(--text-secondary)" strokeWidth="1.8" opacity="0.5">
            <circle cx="12" cy="8" r="3.4" />
            <path d="M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
          </svg>
        )}
      </button>

      <div className={`header-user-menu-dropdown ${open ? 'open' : ''}`}>
          {username && <div className="header-user-menu-username">{username}</div>}
          {role === 'admin' && (
            <button
              type="button"
              className="header-user-menu-item"
              disabled={!!loadingAction}
              onClick={() => {
                setOpen(false);
                window.location.assign('/dashboard');
              }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M4 20V10l8-6 8 6v10" />
                <path d="M9 20v-6h6v6" />
              </svg>
              Dashboard
            </button>
          )}
          <button type="button" className="header-user-menu-item" disabled={!!loadingAction} onClick={() => closeAndNavigate('/perfil')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            lo teu perfil
          </button>
          <button type="button" className="header-user-menu-item"
            disabled={!!loadingAction}
            onClick={async () => {
              setLoadingAction('change-email');
              try {
                const res = await fetch('/api/auth/2fa/challenge', {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    action: 'change-email'
                  })
                });

                const data = await res.json();

                if (data.requiresTwoFactor) {
                  navigate('/verify-2fa', {
                    state: {
                      action: 'change-email',
                      redirectTo: '/account/change-email',
                      fallback: '/'
                    }
                  });
                  return;
                }

                // si no cal 2FA (casos futurs)
                navigate('/account/change-email');
              } finally {
                setLoadingAction(null);
                setOpen(false);
              }
            }}
          >
            {loadingAction === 'change-email' ? (
              <svg className="global-spinner" viewBox="0 0 24 24" width="16" height="16" style={{ width: '16px', height: '16px', borderWidth: '2px', borderColor: 'rgba(255,255,255,0.1)', borderLeftColor: 'currentColor' }}></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M4 7h16v10H4z" />
                <path d="m4 8 8 6 8-6" />
              </svg>
            )}
            Canviar correu
          </button>
          <button type="button" className="header-user-menu-item"
            disabled={!!loadingAction}
            onClick={async () => {
              setLoadingAction('change-password');
              try {
                const res = await fetch('/api/auth/2fa/challenge', {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    action: 'change-password'
                  })
                });

                const data = await res.json();

                if (data.requiresTwoFactor) {
                  navigate('/verify-2fa', {
                    state: {
                      action: 'change-password',
                      redirectTo: '/account/change-password',
                      fallback: '/'
                    }
                  });
                  return;
                }

                // si no cal 2FA (casos futurs)
                navigate('/account/change-password');
              } finally {
                setLoadingAction(null);
                setOpen(false);
              }
            }}
          >
            {loadingAction === 'change-password' ? (
              <svg className="global-spinner" viewBox="0 0 24 24" width="16" height="16" style={{ width: '16px', height: '16px', borderWidth: '2px', borderColor: 'rgba(255,255,255,0.1)', borderLeftColor: 'currentColor' }}></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            )}
            Canviar la contrasenya
          </button>
          <button type="button" className="header-user-menu-item" disabled={!!loadingAction} onClick={() => closeAndNavigate('/account/2fa')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M12 3l7 4v5c0 5-3.5 7.7-7 9-3.5-1.3-7-4-7-9V7l7-4z" />
              <path d="M9.5 12.5 11 14l3.5-3.5" />
            </svg>
            Autenticació en dos passos
          </button>
          {username && (
            <button type="button" className="header-user-menu-item" disabled={!!loadingAction} onClick={() => { setOpen(false); onOpenPinned?.(); }}>
              <TiPin size={17} style={{ transform: 'rotate(45deg)' }} />
              Missatges anclats
            </button>
          )}
          <button type="button" className="header-user-menu-item" disabled={!!loadingAction} onClick={handleTutorialClick}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v1" />
              <path d="M12 12v4" />
            </svg>
            Vore lo tutorial
          </button>
          <button type="button" className="header-user-menu-item theme-toggle-mobile" disabled={!!loadingAction} onClick={toggleTheme}>
            {theme === 'light' ? (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            )}
            Modo {theme === 'light' ? 'fosc' : 'clar'}
          </button>
          <button
            type="button"
            className="header-user-menu-item danger"
            disabled={!!loadingAction}
            onClick={openLogoutModal}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M10 17l5-5-5-5" />
              <path d="M15 12H4" />
              <path d="M20 4v16" />
            </svg>
            Tancar sessió
          </button>
        </div>

      {logoutModalVisible && createPortal(
        <div
          className={`confirm-overlay${logoutClosing ? ' closing' : ''}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeLogoutModal();
            }
          }}
        >
          <div
            className={`confirm-modal${logoutClosing ? ' closing' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
          >
            <p id="logout-confirm-title" className="confirm-title">
              Tancar sessió
            </p>
            <p className="confirm-text">
              Segur que vols tancar la sessió?
            </p>
            <div className="confirm-actions">
              <button type="button" className="confirm-cancel" onClick={closeLogoutModal}>
                Cancel·la
              </button>
              <button type="button" className="confirm-delete" onClick={confirmLogout}>
                Tancar sessió
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {tutorialModalVisible && createPortal(
        <div
          className="confirm-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setTutorialModalVisible(false);
          }}
        >
          <div className="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="tutorial-confirm-title">
            <p id="tutorial-confirm-title" className="confirm-title">Vore lo tutorial</p>
            <p className="confirm-text">Esta opció te redirigirà a la pàgina principal, vols continuar?</p>
            <div className="confirm-actions">
              <button type="button" className="confirm-cancel" onClick={() => setTutorialModalVisible(false)}>
                Cancel·la
              </button>
              <button type="button" className="confirm-delete" onClick={confirmTutorial}>
                Continuar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
